
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useIndexedDB } from "react-indexed-db-hook";
import { useReactMediaRecorder } from "react-media-recorder";
import Dropzone, { IFileWithMeta, IStyleCustomization, StatusValue } from "react-dropzone-uploader";
import { authClient } from "../utils/amplifyClient";
import { Button as MUIButton } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { yellow } from "@mui/material/colors";
import {
    Badge as CSbadge,
    Box,
    Button,
    Checkbox,
    Container,
    Form,
    FormField,
    Grid,
    Header,
    Icon,
    Input,
    Link,
    ProgressBar,
    Select,
    Textarea,
} from './legacy-widgets';
import { InputMode } from "@aws-amplify/ui-react";
import { useNetworkState } from "@uidotdev/usehooks";
import * as Sentry from "@sentry/browser";
import { LoadingSpinner } from "./LoadingSpinner";
import { ReviewReport } from "./ReviewReport";
import { getForm as getFormQuery, listDiscrepancies as listDiscrepanciesQuery } from "../graphql/queries";

import { useUserContext } from "../contexts/UserContext";
import {
    DEFAULT_REPORT_LABELS,
    DEFAULT_FORM_INPUT_TYPES,
    INPUT_MODES,
    ITEM_STATUS_CODES,
    LOCAL_STORAGE_KEYS,
    LOCATION_TYPES,
    OPERATOR_RESPONSE_TYPES,
    REPORT_TYPES,
} from "../utils/constants";
import { appendToReportQueue, computeSummaryCounts, resolveReportUMCounterValue, isReportUMCounterPromptLabel, toAwsDateTime } from "../utils/reportUtils";
import {
    getOperatorResponseChoices,
    mapSectionPrompt,
    OPERATOR_RESPONSE_VALUES,
    requiresNotesOrMedia,
} from "../utils/formPromptMapping";
import { resolveActiveCompanyId } from "../utils/helperFunctions";
import { DailyServiceReportPayload } from "../types/reportTypes";
import { UMCounterLowReadingWarning } from "./UMCounterLowReadingWarning";
import { useOptionalNavigationGuard } from "../contexts/NavigationGuardContext";
import { useCompanyUnitsOfMeasure } from "../hooks/useCompanyUnitsOfMeasure";
import { TEIQspinner } from "./ui";
import { InspectFlowWizard } from "./inspection-flow/InspectFlowWizard";
import { InspectFlowReview } from "./inspection-flow/InspectFlowReview";
import { InspectionDiscardModal } from "./inspection-flow/InspectionDiscardModal";
import {
    canAdvanceFlowStep,
    getFlowStepBlockedReason,
    hasInspectionProgress,
    isFlowWizardNativeStep,
    shouldAutoAdvanceFlowSelection,
} from "./inspection-flow/inspectionWizardUtils";
import { ensureDailyServiceNewIssuesPrompt } from "../utils/dailyServiceNewIssues";
import {
    attachDailyServiceCostSnapshots,
    isDailyServiceCostTrackingEnabled,
} from "../utils/dailyServiceCostTracking";
import { applyUnitConsumableDescriptions } from "../utils/dailyServiceUnitPrompts";
import {
    listServiceItemsByCompanyId,
    listServiceProcedureConsumablesByCompanyId,
} from "../graphql/companyResourceQueries";
import "../css/InspectionFlowWizard.css";

import { iInspectProps, tFormPrompts, tGPSInput, tGPSPosition, tInspectionFormInput, tMediaInput } from "../types";

import "react-dropzone-uploader/dist/styles.css";
import { Key } from "react-indexed-db-hook/lib/indexed-db";
import clsx from "clsx";
import { Stack } from "./ui";

const theme = createTheme({
    palette: {
        secondary: yellow,
    },
});

export const DailyServiceLog = ({
    backBtnAction,
    equipmentData,
    equipmentId,
    selectedUnitId,
    selectedUnit,
    uiVariant = 'legacy',
    equipmentTitle,
    formSubtitle,
    locationLabel,
}: iInspectProps) => {
    const guard = useOptionalNavigationGuard();
    const discardExitModeRef = useRef<'hub' | 'pending' | null>(null);
    const [showDiscardModal, setShowDiscardModal] = useState(false);
    const network = useNetworkState();
    const { company, companyOwner, companyUser } = useUserContext();
    const [activeStepIndex, setActiveStepIndex] = useState<number>(0); // --> Current step of the wizard
    const [isLoading, setIsLoading] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [isLocating, setIsLocating] = useState(false);
    const [userLocation, setUserLocation] = useState<tGPSInput | null>(null);
    const [dailyServiceLogFormPrompts, setDailyServiceLogFormPrompts] = useState<tInspectionFormInput[]>([]);
    const [media, setMedia] = useState<{ [key: number]: tMediaInput[] } | null>(null);
    // const [mediaAction, setMediaAction] = useState<tMediaAction | null>(null);
    const [mediaQueue, setMediaQueue] = useState<{
        fileWithMeta: IFileWithMeta;
        status: StatusValue;
    }[]>([]);
    const [shouldReviewForm, setShouldReviewForm] = useState(false);
    const [alwaysTriggerEmail, setAlwaysTriggerEmail] = useState(false);
    const [audioReview, setAudioReview] = useState<{ [key: number]: Blob }>({});
    const navigate = useNavigate();
    const { add, getByID, deleteRecord } = useIndexedDB("media");

    const companyId = resolveActiveCompanyId({ companyUser, companyOwner, company });
    const { catalogOptions: umCatalog } = useCompanyUnitsOfMeasure(
        companyId ?? '',
        selectedUnit?.unitOfMeasure ? [String(selectedUnit.unitOfMeasure)] : [],
    );

    const getActiveDiscrepancies = async () => {
        try {
            // First get all active discrepancies for this equipment/unit
            const discrepanciesResponse = await authClient.graphql({
                query: listDiscrepanciesQuery,
                variables: {
                    filter: {
                        equipmentId: { eq: equipmentId },
                        ...(selectedUnitId?.value ? { unitId: { eq: selectedUnitId.value } } : {}),
                    }
                }
            });

            return discrepanciesResponse.data.listDiscrepancies.items.filter((issue) => issue.reportId)
        } catch (error) {
            console.log(error);
            Sentry.captureException(error);
            return []
        }
    }

    useEffect(() => {

        if (equipmentData?.id && !dailyServiceLogFormPrompts.length) {
            beginDailyService()
        }
    }, [equipmentData.id, network.online]);

    const beginDailyService = async () => {
        setIsLoading(true);
        try {
            const formPrompts = await getDailyServiceLogForm();
            const activeDiscrepancies = await getActiveDiscrepancies();

            const relevantDiscrepancies = activeDiscrepancies.filter(
                disc => disc.status !== ITEM_STATUS_CODES.ok
            );

            const processedForm = formPrompts.map((prompt: tInspectionFormInput) => {
                const promptKey = `${prompt.title}-${prompt.label}-${prompt.description}`;
                const matchingDiscrepancy = relevantDiscrepancies.find(disc =>
                    `${disc.promptTitle}-${disc.promptLabel}-${disc.promptDescription}` === promptKey
                );

                let discrepancyNotes = '';
                let discrepancyStatus = null;

                if (matchingDiscrepancy) {
                    // Preserve the actual status from the discrepancy
                    discrepancyStatus = matchingDiscrepancy.status

                    if (matchingDiscrepancy.log) {
                        try {
                            const logEntries = JSON.parse(matchingDiscrepancy.log);
                            const relevantEntry = logEntries.find((entry: { notes: string | string[]; }) =>
                                entry && entry.notes && !entry.notes.includes('[Updated Status:')
                            );
                            discrepancyNotes = relevantEntry?.notes || '';
                        } catch (e) {
                            console.error('Error parsing discrepancy log:', e);
                            discrepancyNotes = '';
                        }
                    }
                }

                return {
                    ...prompt,
                    hasActiveDiscrepancy: !!matchingDiscrepancy,
                    discrepancyNotes,
                    discrepancyStatus,
                    // Keep open-issue context for "Status Unchanged" UX, but do not
                    // prefill warn/inop into the new Daily Service report payload.
                    value: prompt.value || '',
                };
            });
            setDailyServiceLogFormPrompts(processedForm);

        } catch (error) {
            console.error('Error processing inspection:', error);
            Sentry.captureException(error);
        } finally {
            setIsLoading(false);
            setIsPageLoading(false);
        }
    };

    const getDailyServiceLogForm = async () => {

        const currentUnitDailyServiceLogId = equipmentData.units?.find(unit => unit?.id === selectedUnitId?.value)?.dailyServiceLogFormId

        try {
            const dailyServiceLogFormResponse =
                network.online && companyId
                    ? await authClient.graphql({
                        query: getFormQuery,
                        variables: { id: (selectedUnitId ? currentUnitDailyServiceLogId : equipmentData.dailyServiceLogFormId) ?? '' },
                    })
                    : JSON.parse(localStorage.getItem(`form_${selectedUnitId ? currentUnitDailyServiceLogId : equipmentData.dailyServiceLogFormId}`) || '{}');

            const prompts: tInspectionFormInput[] = dailyServiceLogFormResponse?.data?.getForm?.noMachineHours
                ? []
                : [
                    {
                        type: DEFAULT_FORM_INPUT_TYPES.standAloneQuestion,
                        label: DEFAULT_REPORT_LABELS.currentMachineHours,
                        inputMode: INPUT_MODES.NUMBER,
                        description: "",
                        value: "",
                        notes: "",
                        photo: [],
                        title: "",
                        hasActiveDiscrepancy: false,
                        discrepancyNotes: "",
                    },
                ];

            if (dailyServiceLogFormResponse?.data?.getForm) {
                setAlwaysTriggerEmail(Boolean(dailyServiceLogFormResponse.data.getForm.alwaysTriggerEmail));
            }

            if (dailyServiceLogFormResponse?.data?.getForm?.formData) {
                JSON.parse(dailyServiceLogFormResponse.data.getForm.formData).forEach(
                    (formData: tFormPrompts) => {
                        if (formData.type === DEFAULT_FORM_INPUT_TYPES.standAloneQuestion) {
                            prompts.push({
                                type: DEFAULT_FORM_INPUT_TYPES.standAloneQuestion,
                                label: formData.prompt,
                                inputMode: formData.inputMode?.value || "text",
                                description: "",
                                value: "",
                                notes: "",
                                photo: [],
                                title: "",
                                hasActiveDiscrepancy: false,
                                discrepancyNotes: "",
                            });
                        } else {
                            formData.prompts?.length && formData.prompts.forEach((prompt) => {
                                prompts.push(mapSectionPrompt(formData.name || '', prompt));
                            });
                        }
                    }
                );
            }

            let nextPrompts = applyUnitConsumableDescriptions(
                ensureDailyServiceNewIssuesPrompt(prompts),
                selectedUnit?.extraUnitInfo,
            );

            if (isDailyServiceCostTrackingEnabled(company, selectedUnit) && companyId && network.online) {
                try {
                    const [catalog, links] = await Promise.all([
                        listServiceItemsByCompanyId(companyId),
                        listServiceProcedureConsumablesByCompanyId(companyId),
                    ]);
                    // Gen2 catalog rows satisfy the fields this util reads; cast at the boundary
                    // until pmTypes.PMPartsAndProcedure is migrated to the schema type (GEN2-TYPE-MIGRATION-PLAN).
                    nextPrompts = attachDailyServiceCostSnapshots(
                        nextPrompts,
                        catalog as unknown as Parameters<typeof attachDailyServiceCostSnapshots>[1],
                        links,
                    );
                } catch (error) {
                    console.error('Failed to snapshot Daily Service catalog prices:', error);
                    Sentry.captureException(error);
                }
            }

            return nextPrompts;

        } catch (error) {
            console.log(error);
            Sentry.captureException(error);
            return [];
        } finally {
            if (uiVariant !== 'flow') {
                setIsPageLoading(false);
            }
        }
    };

    const flashStyle = `
 .flash-warning {
    animation: flashBorder 4s ease 0s infinite normal forwards;
  }

  .flash-warning.warning-status {
    --flash-color: #ffc107;    border: 2px solid var(--flash-color);
  }

  .flash-warning:not(.warning-status) {
    --flash-color: #113ec4;
    border: 2px solid var(--flash-color);
  }

  @keyframes flashBorder {
    0%, 50%, 100% {
      border-color: var(--flash-color);
      box-shadow: 0 0 10px 4px var(--flash-color);
    }
    25%, 75% {
      border-color: var(--flash-color);
      box-shadow: 0 0 1px 0px var(--flash-color);
    }
  }
`;
    // Add the style to your component
    useEffect(() => {
        const styleSheet = document.createElement("style");
        styleSheet.innerText = flashStyle;
        document.head.appendChild(styleSheet);

        return () => {
            document.head.removeChild(styleSheet);
        };
    }, []);

    const getMimeType = () => {
        const types = [
            'audio/webm',
            'audio/mp4',
            'audio/ogg',
            'audio/wav'
        ];

        for (const type of types) {
            if (MediaRecorder.isTypeSupported(type)) {
                return type;
            }
        }

        // If none of the preferred MIME types are supported, 
        // let the browser use its default
        return '';
    };

    const { clearBlobUrl, status, startRecording, stopRecording } =
        useReactMediaRecorder({
            mediaRecorderOptions: {
                mimeType: getMimeType(),
                //switched from audio/wav to fix MIME errors - if this causes a problem can go back
            },
            audio: true,
            onStop: async (_url, blob) => {
                const obj = {
                    name: "audio.wav",
                    file: blob,
                    question: activeStepIndex,
                    formId: equipmentData.dailyServiceLogFormId,
                };
                const event = await add(obj);
                setAudioReview({
                    ...audioReview,
                    [activeStepIndex]: blob,
                });
                setMedia((prev) => {
                    return {
                        ...prev,
                        [activeStepIndex]: (prev && prev[activeStepIndex])
                            ? [
                                ...prev[activeStepIndex],
                                {
                                    id: event,
                                    name: "audio.wav",
                                },
                            ]
                            : [
                                {
                                    id: event,
                                    name: "audio.wav",
                                },
                            ],
                    };
                });
            },
        });

    const coordinates =
        JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.LOCATION) || '{}') || {};

    const createDailyServiceLogReport = () => {
        if (!companyId) {
            return;
        }
        setIsLoading(true);

        // Resolve dailyServiceLogFormId from the selected unit or equipment data
        const currentUnitDailyServiceLogId = equipmentData.units?.find(unit => unit?.id === selectedUnitId?.value)?.dailyServiceLogFormId;
        const dailyServiceLogFormId = selectedUnitId ? currentUnitDailyServiceLogId : equipmentData.dailyServiceLogFormId;

        const machineHoursValue = resolveReportUMCounterValue(dailyServiceLogFormPrompts);

        const { okCount, warnCount, inopCount, totalItems } = computeSummaryCounts(dailyServiceLogFormPrompts);

        const payload: DailyServiceReportPayload = {
            reportType: REPORT_TYPES.DAILY_SERVICE as 'DailyServiceReport',
            companyId: companyId ?? '',
            equipmentCategoryId: equipmentId,
            unitId:
                selectedUnitId?.value ||
                localStorage.getItem(LOCAL_STORAGE_KEYS.UNIT_ID) ||
                undefined,
            userId: '', // Will be set by ReportSent.tsx
            formId: equipmentData.formId || dailyServiceLogFormId || '',
            equipmentCategoryName: equipmentData.name,
            unitName: selectedUnitId?.label,
            userName: '', // Will be set by ReportSent.tsx
            locationId: (equipmentData as any).locationId || selectedUnit?.locationId,
            machineHoursAtService: machineHoursValue,
            umCounterAtService: machineHoursValue,
            dailyServiceLogFormId: dailyServiceLogFormId || '',
            alwaysTriggerEmail,
            costTrackingEnabled: isDailyServiceCostTrackingEnabled(company, selectedUnit),
            gpsLatitude: userLocation?.latitude,
            gpsLongitude: userLocation?.longitude,
            gpsLocationType: userLocation?.type,
            inspectionStarted: toAwsDateTime(localStorage.getItem(LOCAL_STORAGE_KEYS.TIME_INSPECTION_STARTED)),
            okCount,
            warnCount,
            inopCount,
            totalItems,
            responseData: JSON.stringify(dailyServiceLogFormPrompts),
            media: JSON.stringify(media),
        };

        appendToReportQueue(payload);

        localStorage.setItem(LOCAL_STORAGE_KEYS.EQUIPMENT_NAME, equipmentData.name);
        localStorage.removeItem(LOCAL_STORAGE_KEYS.LAST_REPORT)
        setIsLoading(false);
        window.history.pushState(null, '', "/");
        navigate("/report-sent", { replace: true });
    };

    useEffect(() => {
        setLocationFunction();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        localStorage.setItem(LOCAL_STORAGE_KEYS.INSPECT, equipmentId);
        localStorage.setItem(LOCAL_STORAGE_KEYS.EQUIPMENT_NAME, selectedUnitId?.label ? `${equipmentData.name} - ${selectedUnitId.label}` : equipmentData.name)
        localStorage.setItem(LOCAL_STORAGE_KEYS.UNIT_ID, selectedUnitId ? selectedUnitId?.value : "")
        localStorage.setItem(LOCAL_STORAGE_KEYS.SELECTED_UNIT, selectedUnit ? JSON.stringify(selectedUnit) : '')
    }, [equipmentId]);

    useEffect(() => {
        const storePosition = (position: tGPSPosition) => {
            // hasPosition = true;
            setUserLocation({
                type: LOCATION_TYPES.gps,
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
            });
            localStorage.setItem(
                LOCAL_STORAGE_KEYS.LOCATION,
                JSON.stringify({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                })
            );
            setIsLocating(false);
        };

        if (isLocating) {
            navigator.geolocation.getCurrentPosition(
                storePosition,
                () => {
                    setUserLocation({
                        type: coordinates?.latitude
                            ? LOCATION_TYPES.lastKnownPosition
                            : LOCATION_TYPES.unknown,
                        ...coordinates,
                    });
                    setIsLocating(false);
                },
                { timeout: 10000 }
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLocating]);

    useEffect(() => {
        const processMediaQueue = async () => {
            if (mediaQueue.length === 0) return;

            const [currentMedia, ...remainingQueue] = mediaQueue;

            if (currentMedia.status === "removed") {
                if (!media) return;

                const recordToDelete = media[activeStepIndex]?.find(
                    (el) => el.name === currentMedia.fileWithMeta.file.name
                )?.id;

                if (recordToDelete) {
                    await deleteRecord(recordToDelete);
                    setMedia((prev) => ({
                        ...prev,
                        [activeStepIndex]: media[activeStepIndex].filter(
                            (el) => el.name !== currentMedia.fileWithMeta.file.name
                        ),
                    }));
                }
            } else if (currentMedia.status === "done") {
                try {
                    const obj = {
                        name: currentMedia.fileWithMeta.file.name,
                        file: currentMedia.fileWithMeta.file,
                        question: activeStepIndex,
                        formId: equipmentData.dailyServiceLogFormId,
                    };
                    const event = await add(obj);
                    const photoData = await getByID(event);

                    setMedia((prev) => ({
                        ...prev,
                        [activeStepIndex]: (prev && prev[activeStepIndex])
                            ? [
                                ...prev[activeStepIndex],
                                {
                                    id: event,
                                    name: currentMedia.fileWithMeta.file.name,
                                    data: photoData,
                                },
                            ]
                            : [
                                {
                                    id: event,
                                    name: currentMedia.fileWithMeta.file.name,
                                    data: photoData,
                                },
                            ],
                    }));
                } catch (error) {
                    console.log(error);
                    Sentry.captureException(error);
                }
            }

            setMediaQueue(remainingQueue);
        };

        processMediaQueue();
    }, [mediaQueue, activeStepIndex, media, equipmentData.dailyServiceLogFormId, add, getByID, deleteRecord]);

    const setLocationFunction = async () => {
        if (navigator.geolocation) {
            setIsLocating(true);
        } else {
            if (coordinates?.latitude) {
                setUserLocation({
                    type: LOCATION_TYPES.lastKnownPosition,
                    ...coordinates,
                });
            }
        }
    };

    useEffect(() => {
        if (!dailyServiceLogFormPrompts[activeStepIndex]) return;

        const currentPrompt = dailyServiceLogFormPrompts[activeStepIndex];

        // Only update if:
        // 1. The prompt has an active discrepancy
        // 2. No new notes have been added
        // 3. There are discrepancy notes to use
        // 4. Notes haven't been modified yet
        if (
            currentPrompt.hasActiveDiscrepancy &&
            !currentPrompt.notes &&
            currentPrompt.discrepancyNotes &&
            !currentPrompt.notesModified
        ) {
            const updatePrompts = [...dailyServiceLogFormPrompts];
            updatePrompts[activeStepIndex] = {
                ...currentPrompt,
                notes: currentPrompt.discrepancyNotes,
                notesModified: false // Track that these are original discrepancy notes
            };
            setDailyServiceLogFormPrompts(updatePrompts);
        }
    }, [activeStepIndex, dailyServiceLogFormPrompts]);

    const handleChangeStatus = (fileWithMeta: IFileWithMeta, dropZoneStatus: StatusValue) => {
        if (dropZoneStatus === "removed" || dropZoneStatus === "done") {
            setMediaQueue(prev => [...prev, {
                fileWithMeta,
                status: dropZoneStatus
            }]);
        }
    };
    const isAudioRecordingStopped = (stateOfRecorder: string) => {
        return stateOfRecorder === "stopped" || stateOfRecorder === "idle";
    };

    const getLocationStatusColor = () => {
        switch (userLocation?.type) {
            case LOCATION_TYPES.gps:
                return "green";
            case LOCATION_TYPES.lastKnownPosition:
                return "blue";
            case null:
            default:
                return "grey";
        }
    };

    const currentPrompt = dailyServiceLogFormPrompts[activeStepIndex];
    const useFlowWizard =
        uiVariant === 'flow' &&
        !shouldReviewForm &&
        dailyServiceLogFormPrompts.length > 0 &&
        currentPrompt &&
        isFlowWizardNativeStep(currentPrompt);

    const dailyServiceHasProgress = hasInspectionProgress(
        dailyServiceLogFormPrompts,
        activeStepIndex,
        shouldReviewForm,
        media,
        audioReview,
    );

    const canGoNextStep = canAdvanceFlowStep(currentPrompt, media, activeStepIndex);

    const clearDailyServiceSessionMedia = async () => {
        if (!media) {
            return;
        }

        const mediaIds = Object.values(media)
            .flat()
            .map((item) => item.id)
            .filter((id): id is number => id != null);

        for (const id of mediaIds) {
            try {
                await deleteRecord(id);
            } catch (error) {
                console.error('Failed to delete daily service media record', error);
                Sentry.captureException(error);
            }
        }
    };

    const resetDailyServiceWizardState = () => {
        clearBlobUrl();
        setMedia(null);
        setAudioReview({});
        setMediaQueue([]);
        setActiveStepIndex(0);
        setShouldReviewForm(false);
        setDailyServiceLogFormPrompts([]);
    };

    const discardDailyServiceSession = async () => {
        await clearDailyServiceSessionMedia();
        resetDailyServiceWizardState();
        guard?.registerGuard(null);
    };

    const restartDailyService = async () => {
        await discardDailyServiceSession();
        guard?.cancelPendingNavigation();
        setShowDiscardModal(false);
        discardExitModeRef.current = null;
        await beginDailyService();
    };

    const exitDailyServiceToMenu = async () => {
        const exitMode = discardExitModeRef.current;
        await discardDailyServiceSession();
        setShowDiscardModal(false);
        discardExitModeRef.current = null;

        if (exitMode === 'pending') {
            guard?.confirmPendingNavigation();
            return;
        }

        backBtnAction();
    };

    const requestExitDailyService = () => {
        if (!dailyServiceHasProgress) {
            void exitDailyServiceToMenu();
            return;
        }

        discardExitModeRef.current = 'hub';
        setShowDiscardModal(true);
    };

    const requestStartOver = () => {
        if (!dailyServiceHasProgress) {
            return;
        }

        discardExitModeRef.current = null;
        setShowDiscardModal(true);
    };

    useEffect(() => {
        if (!guard || uiVariant !== 'flow' || !dailyServiceLogFormPrompts.length) {
            return;
        }

        guard.registerGuard(
            dailyServiceHasProgress
                ? {
                      when: true,
                      onBlocked: () => {
                          discardExitModeRef.current = 'pending';
                          setShowDiscardModal(true);
                      },
                  }
                : null,
        );

        return () => guard.registerGuard(null);
    }, [guard, dailyServiceHasProgress, dailyServiceLogFormPrompts.length, uiVariant]);

    const attachMediaToCurrentPrompt = () => {
        if (media && media[activeStepIndex]) {
            media[activeStepIndex].forEach((mediaItem) => {
                if (!dailyServiceLogFormPrompts[activeStepIndex]?.photo?.includes(mediaItem)) {
                    dailyServiceLogFormPrompts[activeStepIndex]?.photo?.push(mediaItem);
                }
            });
        }
    };

    const advanceFlowStep = () => {
        clearBlobUrl();
        attachMediaToCurrentPrompt();

        if (activeStepIndex < dailyServiceLogFormPrompts.length - 1) {
            setActiveStepIndex(activeStepIndex + 1);
            return;
        }

        setShouldReviewForm(true);
    };

    const handleFlowPrevious = () => {
        clearBlobUrl();
        setActiveStepIndex((index) => Math.max(0, index - 1));
    };

    const handleFlowNext = () => {
        advanceFlowStep();
    };

    const handlePromptValueChange = (index: number, value: string) => {
        setDailyServiceLogFormPrompts((prev) => {
            const updatePrompts = [...prev];
            if (!updatePrompts[index]) return prev;
            updatePrompts[index] = { ...updatePrompts[index], value };
            return updatePrompts;
        });
    };

    const handleFlowPromptValueChange = (index: number, value: string) => {
        const prompt = dailyServiceLogFormPrompts[index];
        handlePromptValueChange(index, value);

        if (index !== activeStepIndex || !shouldAutoAdvanceFlowSelection({ ...prompt, value }, value)) {
            return;
        }

        advanceFlowStep();
    };

    const handlePromptNotesChange = (index: number, notes: string) => {
        setDailyServiceLogFormPrompts((prev) => {
            const updatePrompts = [...prev];
            const current = updatePrompts[index];
            if (!current) return prev;
            updatePrompts[index] = {
                ...current,
                notes,
                notesModified: true,
                ...(current.subType === OPERATOR_RESPONSE_TYPES.NEW_ISSUES &&
                notes.trim() &&
                current.value !== OPERATOR_RESPONSE_VALUES.noNewIssues
                    ? { value: ITEM_STATUS_CODES.warn }
                    : {}),
            };
            return updatePrompts;
        });
    };

    const handleFlowPromptCheckboxToggle = (index: number, option: string) => {
        const updatePrompts = [...dailyServiceLogFormPrompts];
        const currentSelected = updatePrompts[index]?.selectedOptions || [];

        if (currentSelected.includes(option)) {
            updatePrompts[index] = {
                ...updatePrompts[index],
                selectedOptions: currentSelected.filter((val: string) => val !== option),
            };
        } else {
            updatePrompts[index] = {
                ...updatePrompts[index],
                selectedOptions: [...currentSelected, option],
            };
        }

        setDailyServiceLogFormPrompts(updatePrompts);
    };

    const getFlowStepMediaItems = (): tMediaInput[] => {
        const prompt = dailyServiceLogFormPrompts[activeStepIndex];
        const fromMedia = media?.[activeStepIndex] ?? [];
        const fromPrompt = prompt?.photo ?? [];
        const seen = new Set<number>();
        const combined: tMediaInput[] = [];

        for (const item of [...fromPrompt, ...fromMedia]) {
            if (item?.id != null && !seen.has(item.id)) {
                seen.add(item.id);
                combined.push(item);
            }
        }

        return combined;
    };

    const nextStepBlockedReason =
        !canGoNextStep && !isLoading
            ? getFlowStepBlockedReason(currentPrompt, getFlowStepMediaItems())
            : null;

    const handleFlowAddFiles = async (files: FileList) => {
        for (const file of Array.from(files)) {
            const obj = {
                name: file.name,
                file,
                question: activeStepIndex,
                formId: equipmentData.dailyServiceLogFormId,
            };
            const id = await add(obj);
            setMedia((prev) => ({
                ...prev,
                [activeStepIndex]: [...(prev?.[activeStepIndex] ?? []), { ...obj, id }],
            }));
        }
    };

    const handleFlowRemoveMediaItem = async (item: tMediaInput) => {
        if (item.id) {
            await deleteRecord(item.id);
        }

        setMedia((prev) => ({
            ...prev,
            [activeStepIndex]: (prev?.[activeStepIndex] ?? []).filter((el) => el.id !== item.id),
        }));

        const updatePrompts = [...dailyServiceLogFormPrompts];
        if (updatePrompts[activeStepIndex]?.photo?.length) {
            updatePrompts[activeStepIndex] = {
                ...updatePrompts[activeStepIndex],
                photo: updatePrompts[activeStepIndex].photo?.filter((el) => el.id !== item.id),
            };
            setDailyServiceLogFormPrompts(updatePrompts);
        }
    };

    const handleFlowRemoveAudio = async () => {
        clearBlobUrl();

        const audioItems = media?.[activeStepIndex]?.filter((el) => el.name === 'audio.wav') ?? [];
        for (const item of audioItems) {
            if (item.id) {
                await deleteRecord(item.id);
            }
        }

        setMedia((prev) => ({
            ...prev,
            [activeStepIndex]: (prev?.[activeStepIndex] ?? []).filter((el) => el.name !== 'audio.wav'),
        }));

        setAudioReview((prev) => {
            const next = { ...prev };
            delete next[activeStepIndex];
            return next;
        });
    };

    const resolvedEquipmentTitle =
        equipmentTitle || selectedUnit?.name || equipmentData?.name || 'Daily Service';
    const resolvedFormSubtitle = formSubtitle || 'Daily Service Form';
    const resolvedLocationLabel = locationLabel || 'Unassigned';

    if (uiVariant === 'flow' && isPageLoading) {
        return (
            <div className="page-container inspection-flow-wizard-page" aria-busy="true">
                <TEIQspinner variant="lg" label="Loading daily service" />
            </div>
        );
    }

    if (uiVariant === 'flow' && shouldReviewForm && dailyServiceLogFormPrompts.length > 0) {
        return (
                    <div className="page-container inspection-flow-wizard-page">
                        <InspectFlowReview
                            equipmentTitle={resolvedEquipmentTitle}
                            formSubtitle={resolvedFormSubtitle}
                            prompts={dailyServiceLogFormPrompts}
                            isSubmitting={isLoading}
                            flowKind="dailyService"
                            onExit={requestExitDailyService}
                            onGoBack={() => setShouldReviewForm(false)}
                            onSubmit={createDailyServiceLogReport}
                        />
                        <InspectionDiscardModal
                            visible={showDiscardModal}
                            flowKind="dailyService"
                            onDismiss={() => {
                                guard?.cancelPendingNavigation();
                                discardExitModeRef.current = null;
                                setShowDiscardModal(false);
                            }}
                            onStartOver={() => {
                                void restartDailyService();
                            }}
                            onExit={() => {
                                void exitDailyServiceToMenu();
                            }}
                        />
                    </div>
        );
    }

    if (useFlowWizard) {
        return (
                    <div className="page-container inspection-flow-wizard-page">
                        <InspectFlowWizard
                            equipmentTitle={resolvedEquipmentTitle}
                            formSubtitle={resolvedFormSubtitle}
                            locationLabel={resolvedLocationLabel}
                            prompts={dailyServiceLogFormPrompts}
                            activeStepIndex={activeStepIndex}
                            userLocation={userLocation}
                            selectedUnit={selectedUnit}
                            umCatalog={umCatalog}
                            isLoading={isLoading}
                            canGoNext={canGoNextStep}
                            nextStepBlockedReason={nextStepBlockedReason}
                            showStartOver={dailyServiceHasProgress}
                            flowKind="dailyService"
                            onExit={requestExitDailyService}
                            onStartOver={requestStartOver}
                            onPrevious={handleFlowPrevious}
                            onNext={handleFlowNext}
                            onPromptValueChange={handleFlowPromptValueChange}
                            onPromptNotesChange={handlePromptNotesChange}
                            onPromptCheckboxToggle={handleFlowPromptCheckboxToggle}
                            stepMediaItems={getFlowStepMediaItems()}
                            audioBlob={audioReview[activeStepIndex]}
                            recorderStatus={status}
                            onFilesSelected={handleFlowAddFiles}
                            onStartRecording={startRecording}
                            onStopRecording={stopRecording}
                            onRemoveAudio={handleFlowRemoveAudio}
                            onRemoveMediaItem={handleFlowRemoveMediaItem}
                        />
                        <InspectionDiscardModal
                            visible={showDiscardModal}
                            flowKind="dailyService"
                            onDismiss={() => {
                                guard?.cancelPendingNavigation();
                                discardExitModeRef.current = null;
                                setShowDiscardModal(false);
                            }}
                            onStartOver={() => {
                                void restartDailyService();
                            }}
                            onExit={() => {
                                void exitDailyServiceToMenu();
                            }}
                        />
                    </div>
        );
    }

    if (uiVariant === 'flow') {
        return (
            <div className="page-container inspection-flow-wizard-page" aria-busy="true">
                <TEIQspinner variant="lg" label="Loading daily service" />
            </div>
        );
    }

    return (
        <LoadingSpinner
            isLoading={isPageLoading}
            content={
                <ThemeProvider theme={theme}>
                    <Grid
                        gridDefinition={[
                            { colspan: { default: 12 } },
                            { colspan: { default: 12 } },
                        ]}
                    >
                        {equipmentData?.name && (
                            <Header
                                description={
                                    <CSbadge color={`${getLocationStatusColor()}`}>
                                        Location:{" "}
                                        {userLocation?.type || "Getting coordinates from device..."}
                                    </CSbadge>
                                }
                                variant="h1"
                                actions={<Link onFollow={() => backBtnAction()}>Back</Link>}
                            >{`${equipmentData.name}${selectedUnitId ? ` - ${selectedUnitId.label}` : ""
                                } Service Log`}</Header>
                        )}
                        {/* <Flex justifyContent="center">
              {equipmentData?.image ? (
                <img
                  className="equipment-image"
                  src={equipmentData?.image}
                  alt={equipmentData?.name}
                />
              ) : (
                <></>
              )}
            </Flex> */}
                    </Grid>
                    {shouldReviewForm && (
                        <ReviewReport
                            cancel={() => setShouldReviewForm(false)}
                            inspectionFormPrompts={dailyServiceLogFormPrompts}
                            media={media}
                            submit={createDailyServiceLogReport}
                        />
                    )}
                    {!shouldReviewForm && (
                        <>
                            <Stack size="l">
                                <Box color="text-status-success" margin={{ top: "l" }}>
                                    Progress
                                    <ProgressBar
                                        value={
                                            (activeStepIndex / dailyServiceLogFormPrompts.length) * 100 || 0
                                        }
                                    // label="Progress"
                                    />
                                </Box>
                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                    }}
                                >
                                    <Form
                                        actions={
                                            <Stack direction="horizontal" size="m">
                                                {activeStepIndex > 0 && (
                                                    <Button
                                                        variant="normal"
                                                        onClick={() => {
                                                            clearBlobUrl();
                                                            setActiveStepIndex(activeStepIndex - 1);
                                                        }}
                                                    >
                                                        Previous
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="primary"
                                                    disabled={(() => {
                                                        const current = dailyServiceLogFormPrompts[activeStepIndex];
                                                        if (!current) return true;
                                                        if (
                                                            current.subType === DEFAULT_FORM_INPUT_TYPES.checkbox ||
                                                            current.subType === DEFAULT_FORM_INPUT_TYPES.dropdown
                                                        ) {
                                                            return false;
                                                        }
                                                        if (!current.value) return true;
                                                        if (!requiresNotesOrMedia(current)) return false;
                                                        const hasNotes = Boolean(current.notes && current.notes.length > 0);
                                                        const hasMedia = Boolean(media && media[activeStepIndex]?.length);
                                                        return !(hasNotes || hasMedia);
                                                    })()}
                                                    loading={isLoading}
                                                    onClick={() => {
                                                        clearBlobUrl();
                                                        if (media && media[activeStepIndex]) {
                                                            media[activeStepIndex].forEach(async (mediaItem) => {
                                                                if (!dailyServiceLogFormPrompts[activeStepIndex]?.photo?.includes(mediaItem)) {
                                                                    dailyServiceLogFormPrompts[activeStepIndex]?.photo?.push(mediaItem)
                                                                }
                                                            });
                                                        }
                                                        activeStepIndex < dailyServiceLogFormPrompts.length - 1
                                                            ? setActiveStepIndex(activeStepIndex + 1)
                                                            : setShouldReviewForm(true);
                                                    }}
                                                >
                                                    {activeStepIndex < dailyServiceLogFormPrompts.length - 1
                                                        ? "Next"
                                                        : "Review and Sign"}
                                                </Button>
                                            </Stack>
                                        }
                                    >

                                        {dailyServiceLogFormPrompts.map((prompt, idx) => {
                                            if (idx !== activeStepIndex) {
                                                return null;
                                            }
                                            return (
                                                <Box
                                                    key={prompt.label}
                                                    display={idx === activeStepIndex ? "block" : "none"}
                                                >

                                                    {prompt.type === DEFAULT_FORM_INPUT_TYPES.standAloneQuestion && (
                                                        <Container>
                                                            <Stack size="s">
                                                                <FormField
                                                                    label={prompt.label}
                                                                    description={prompt.description}
                                                                >
                                                                    {prompt.inputMode !== INPUT_MODES.TEXTAREA && (
                                                                        <Input
                                                                            autoFocus
                                                                            value={prompt.value}
                                                                            onChange={({ detail }) => {
                                                                                const updatePrompts = [...dailyServiceLogFormPrompts];
                                                                                updatePrompts[idx].value = detail.value;
                                                                                setDailyServiceLogFormPrompts(updatePrompts);
                                                                            }}
                                                                            inputMode={prompt.inputMode as InputMode | undefined}
                                                                            type={prompt.inputMode === INPUT_MODES.NUMBER ? "number" : "text"}
                                                                        />
                                                                    )}
                                                                    {prompt.inputMode === INPUT_MODES.TEXTAREA && (
                                                                        <Textarea
                                                                            autoFocus
                                                                            value={prompt.value}
                                                                            onChange={({ detail }) => {
                                                                                const updatePrompts = [...dailyServiceLogFormPrompts];
                                                                                updatePrompts[idx].value = detail.value;
                                                                                setDailyServiceLogFormPrompts(updatePrompts);
                                                                            }}
                                                                        />
                                                                    )}
                                                                </FormField>
                                                                {isReportUMCounterPromptLabel(prompt.label) && (
                                                                    <UMCounterLowReadingWarning
                                                                        enteredValue={prompt.value}
                                                                        unit={
                                                                            selectedUnit ||
                                                                            equipmentData.units?.find(
                                                                                (unit) => unit?.id === selectedUnitId?.value,
                                                                            )
                                                                        }
                                                                        unitOfMeasure={
                                                                            selectedUnit?.unitOfMeasure ||
                                                                            equipmentData.units?.find(
                                                                                (unit) => unit?.id === selectedUnitId?.value,
                                                                            )?.unitOfMeasure
                                                                        }
                                                                    />
                                                                )}
                                                            </Stack>
                                                        </Container>
                                                    )}
                                                    {prompt.type === DEFAULT_FORM_INPUT_TYPES.section && (
                                                        <>
                                                            <Container
                                                                header={<Header variant="h2">{prompt.title}</Header>}
                                                                className={clsx({
                                                                    'flash-warning': prompt.hasActiveDiscrepancy && prompt.discrepancyStatus !== "ok",
                                                                    'warning-status': prompt.discrepancyStatus === ITEM_STATUS_CODES.warn
                                                                })}
                                                            >
                                                                <Stack direction="vertical" size="xxs">
                                                                    {prompt.subType === (DEFAULT_FORM_INPUT_TYPES.fourResponse || DEFAULT_FORM_INPUT_TYPES.dropdown) &&
                                                                        <Box variant="h5">What you are inspecting:</Box>
                                                                    }
                                                                    <div className="flash">
                                                                        <h3>{prompt.label}</h3>
                                                                    </div>
                                                                    {(prompt.partNumber ||
                                                                        prompt.vendor ||
                                                                        prompt.partDescription ||
                                                                        prompt.defaultUM) && (
                                                                        <Box variant="p" color="text-body-secondary">
                                                                            {[
                                                                                prompt.partNumber
                                                                                    ? `#${prompt.partNumber}`
                                                                                    : null,
                                                                                prompt.partDescription || prompt.vendor,
                                                                                prompt.defaultUM
                                                                                    ? `UM: ${prompt.defaultUM}`
                                                                                    : null,
                                                                            ]
                                                                                .filter(Boolean)
                                                                                .join(' · ')}
                                                                        </Box>
                                                                    )}
                                                                    {prompt.description && (
                                                                        <>
                                                                            {prompt.subType !== DEFAULT_FORM_INPUT_TYPES.checkbox &&
                                                                                <Box variant="p">What you are looking for:</Box>}
                                                                            <div className="flash">
                                                                                <p>{prompt.description}</p>
                                                                            </div>
                                                                        </>
                                                                    )
                                                                    }
                                                                    {prompt.hasActiveDiscrepancy && (
                                                                        <Box
                                                                            variant="p"
                                                                            color="text-status-warning"
                                                                            padding={{ top: 'xs', bottom: 'xs' }}
                                                                        >
                                                                            <div style={{
                                                                                backgroundColor: '#fafafa',
                                                                                padding: '10px',
                                                                                borderRadius: '4px',
                                                                                border: '1px solid #eaeded'
                                                                            }}>
                                                                                <strong>Previous Report Notes:</strong><br />
                                                                                {prompt.discrepancyNotes || 'No notes provided'}
                                                                            </div>
                                                                        </Box>
                                                                    )}

                                                                    {prompt.subType === DEFAULT_FORM_INPUT_TYPES.yesNo && (
                                                                        <Grid
                                                                            gridDefinition={[
                                                                                { colspan: { "default": 6, s: 3 } },
                                                                                { colspan: { "default": 6, s: 3 } },
                                                                                { colspan: { "default": 6, s: 3 } },
                                                                                ...((!prompt.hasActiveDiscrepancy || prompt.discrepancyStatus !== ITEM_STATUS_CODES.warn) ?
                                                                                    [{ colspan: { "default": 6, s: 3 } }] : [])
                                                                            ]}
                                                                        >
                                                                            <MUIButton
                                                                                variant={!prompt.value || prompt.value === ITEM_STATUS_CODES.ok ? "contained" : "outlined"}
                                                                                color="success"
                                                                                style={{
                                                                                    borderRadius: 20,
                                                                                    fontSize: 40,
                                                                                    padding: "20px 10px",
                                                                                    width: "100%",
                                                                                }}
                                                                                onClick={() => {
                                                                                    const updatePrompts = [...dailyServiceLogFormPrompts];
                                                                                    updatePrompts[idx].value = ITEM_STATUS_CODES.ok;
                                                                                    setDailyServiceLogFormPrompts(updatePrompts);
                                                                                    activeStepIndex < dailyServiceLogFormPrompts.length - 1
                                                                                        ? setActiveStepIndex(activeStepIndex + 1)
                                                                                        : setShouldReviewForm(true);
                                                                                }}
                                                                            >
                                                                                Yes
                                                                            </MUIButton>
                                                                            <MUIButton
                                                                                variant={
                                                                                    !prompt.value ||
                                                                                    prompt.value === ITEM_STATUS_CODES.warn ||
                                                                                    prompt.value === ITEM_STATUS_CODES.status_unchanged
                                                                                        ? "contained"
                                                                                        : "outlined"
                                                                                }
                                                                                color="secondary"
                                                                                style={{
                                                                                    borderRadius: 20,
                                                                                    fontSize: prompt.hasActiveDiscrepancy && prompt.discrepancyStatus === ITEM_STATUS_CODES.warn ? '14px' : 40, padding: "20px 10px",
                                                                                    width: "100%",
                                                                                }}
                                                                                onClick={() => {
                                                                                    const updatePrompts = [...dailyServiceLogFormPrompts];
                                                                                    updatePrompts[idx].value =
                                                                                        prompt.hasActiveDiscrepancy &&
                                                                                        prompt.discrepancyStatus === ITEM_STATUS_CODES.warn
                                                                                            ? ITEM_STATUS_CODES.status_unchanged
                                                                                            : ITEM_STATUS_CODES.warn;
                                                                                    setDailyServiceLogFormPrompts(updatePrompts);
                                                                                }}
                                                                            >
                                                                                {prompt.hasActiveDiscrepancy && prompt.discrepancyStatus === ITEM_STATUS_CODES.warn
                                                                                    ? `Status Unchanged`
                                                                                    : `No`}

                                                                            </MUIButton>
                                                                        </Grid>
                                                                    )}

                                                                    {prompt.subType === DEFAULT_FORM_INPUT_TYPES.checkbox && (
                                                                        <Stack direction="vertical" size="xs">
                                                                            {prompt.options?.map((option: string, optionIdx: number) => (
                                                                                <Checkbox
                                                                                    key={optionIdx}
                                                                                    checked={prompt.selectedOptions?.includes(option) || false}
                                                                                    onChange={({ detail }) => {
                                                                                        const updatePrompts = [...dailyServiceLogFormPrompts];
                                                                                        const currentSelected = updatePrompts[idx].selectedOptions || [];

                                                                                        if (detail.checked) {
                                                                                            updatePrompts[idx].selectedOptions = [...currentSelected, option];
                                                                                        } else {
                                                                                            updatePrompts[idx].selectedOptions = currentSelected.filter((val: string) => val !== option);
                                                                                        }
                                                                                        setDailyServiceLogFormPrompts(updatePrompts);
                                                                                    }}
                                                                                >
                                                                                    {option}
                                                                                </Checkbox>
                                                                            ))}
                                                                        </Stack>
                                                                    )}

                                                                    {prompt.subType === DEFAULT_FORM_INPUT_TYPES.sectionQuestion && (
                                                                        <>
                                                                            <Container>
                                                                                {/* <FormField
                                          label={prompt.label}
                                          description={prompt.description}
                                        > */}
                                                                                {prompt.inputMode !== INPUT_MODES.TEXTAREA && (
                                                                                    <Input
                                                                                        autoFocus
                                                                                        value={prompt.value}
                                                                                        onChange={({ detail }) => {
                                                                                            const updatePrompts = [...dailyServiceLogFormPrompts];
                                                                                            updatePrompts[idx].value = detail.value;
                                                                                            setDailyServiceLogFormPrompts(updatePrompts);
                                                                                        }}
                                                                                        inputMode={prompt.inputMode as InputMode | undefined}
                                                                                        type={prompt.inputMode === INPUT_MODES.NUMBER ? "number" : "text"}
                                                                                    />
                                                                                )}
                                                                                {prompt.inputMode === INPUT_MODES.TEXTAREA && (
                                                                                    <Textarea
                                                                                        autoFocus
                                                                                        value={prompt.value}
                                                                                        onChange={({ detail }) => {
                                                                                            const updatePrompts = [...dailyServiceLogFormPrompts];
                                                                                            updatePrompts[idx].value = detail.value;
                                                                                            setDailyServiceLogFormPrompts(updatePrompts);
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                                {/* </FormField> */}
                                                                            </Container>
                                                                        </>
                                                                    )}

                                                                    {prompt.subType === DEFAULT_FORM_INPUT_TYPES.dropdown && (
                                                                        <Select
                                                                            selectedOption={prompt.value ? { label: prompt.value, value: prompt.value } : null}
                                                                            onChange={({ detail }) => {
                                                                                const updatePrompts = [...dailyServiceLogFormPrompts];
                                                                                updatePrompts[idx].value = detail.selectedOption.value ?? '';
                                                                                setDailyServiceLogFormPrompts(updatePrompts);
                                                                            }}
                                                                            options={prompt.options?.map((option: string) => ({
                                                                                label: option, // Use string as both label and value
                                                                                value: option
                                                                            })) || []}
                                                                            placeholder="Select an option"
                                                                            expandToViewport
                                                                        />
                                                                    )}

                                                                    {prompt.subType === OPERATOR_RESPONSE_TYPES.QUANTITY_ONLY && (
                                                                        <FormField
                                                                            label={prompt.defaultUM ? `Quantity (${prompt.defaultUM})` : 'Quantity'}
                                                                        >
                                                                            <Input
                                                                                autoFocus
                                                                                type="number"
                                                                                inputMode="decimal"
                                                                                value={prompt.value}
                                                                                onChange={({ detail }) => {
                                                                                    const updatePrompts = [...dailyServiceLogFormPrompts];
                                                                                    updatePrompts[idx].value = detail.value;
                                                                                    setDailyServiceLogFormPrompts(updatePrompts);
                                                                                }}
                                                                            />
                                                                        </FormField>
                                                                    )}

                                                                    {(() => {
                                                                        const choices = getOperatorResponseChoices(prompt.subType);
                                                                        if (!choices?.length) return null;
                                                                        return (
                                                                        <Grid
                                                                            gridDefinition={choices.map(() => ({
                                                                                colspan: { default: 6, s: 3 },
                                                                            }))}
                                                                        >
                                                                            {choices.map((choice) => (
                                                                                <MUIButton
                                                                                    key={choice.value}
                                                                                    variant={prompt.value === choice.value ? 'contained' : 'outlined'}
                                                                                    color="primary"
                                                                                    aria-pressed={prompt.value === choice.value}
                                                                                    style={{
                                                                                        borderRadius: 20,
                                                                                        fontSize: 18,
                                                                                        padding: '20px 10px',
                                                                                        width: '100%',
                                                                                        textTransform: 'none',
                                                                                    }}
                                                                                    onClick={() => {
                                                                                        const updatePrompts = [...dailyServiceLogFormPrompts];
                                                                                        updatePrompts[idx].value = choice.value;
                                                                                        setDailyServiceLogFormPrompts(updatePrompts);
                                                                                        const shouldAdvance =
                                                                                            choice.autoAdvance &&
                                                                                            !(
                                                                                                updatePrompts[idx].autoFlag &&
                                                                                                requiresNotesOrMedia(updatePrompts[idx])
                                                                                            );
                                                                                        if (shouldAdvance) {
                                                                                            activeStepIndex < dailyServiceLogFormPrompts.length - 1
                                                                                                ? setActiveStepIndex(activeStepIndex + 1)
                                                                                                : setShouldReviewForm(true);
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    {choice.label}
                                                                                </MUIButton>
                                                                            ))}
                                                                        </Grid>
                                                                        );
                                                                    })()}

                                                                    {(!prompt.subType || prompt.subType === DEFAULT_FORM_INPUT_TYPES.fourResponse) && (
                                                                        <Grid
                                                                            gridDefinition={[
                                                                                { colspan: { "default": 6, s: 3 } },
                                                                                { colspan: { "default": 6, s: 3 } },
                                                                                { colspan: { "default": 6, s: 3 } },
                                                                                ...((!prompt.hasActiveDiscrepancy || prompt.discrepancyStatus !== ITEM_STATUS_CODES.warn) ?
                                                                                    [{ colspan: { "default": 6, s: 3 } }] : [])
                                                                            ]}
                                                                        >
                                                                            <MUIButton
                                                                                variant={!prompt.value || prompt.value === ITEM_STATUS_CODES.ok ? "contained" : "outlined"}
                                                                                color="success"
                                                                                style={{
                                                                                    borderRadius: 20,
                                                                                    fontSize: 40,
                                                                                    padding: "20px 10px",
                                                                                    width: "100%",
                                                                                }}
                                                                                onClick={() => {
                                                                                    const updatePrompts = [...dailyServiceLogFormPrompts];
                                                                                    updatePrompts[idx].value = ITEM_STATUS_CODES.ok;
                                                                                    setDailyServiceLogFormPrompts(updatePrompts);
                                                                                    activeStepIndex < dailyServiceLogFormPrompts.length - 1
                                                                                        ? setActiveStepIndex(activeStepIndex + 1)
                                                                                        : setShouldReviewForm(true);
                                                                                }}
                                                                            >
                                                                                <Icon name="status-positive" size="medium" />
                                                                            </MUIButton>
                                                                            <MUIButton
                                                                                variant={
                                                                                    !prompt.value ||
                                                                                    prompt.value === ITEM_STATUS_CODES.warn ||
                                                                                    prompt.value === ITEM_STATUS_CODES.status_unchanged
                                                                                        ? "contained"
                                                                                        : "outlined"
                                                                                }
                                                                                color="secondary"
                                                                                style={{
                                                                                    borderRadius: 20,
                                                                                    fontSize: prompt.hasActiveDiscrepancy && prompt.discrepancyStatus === ITEM_STATUS_CODES.warn ? '14px' : 40, padding: "20px 10px",
                                                                                    width: "100%",
                                                                                }}
                                                                                onClick={() => {
                                                                                    const updatePrompts = [...dailyServiceLogFormPrompts];
                                                                                    updatePrompts[idx].value =
                                                                                        prompt.hasActiveDiscrepancy &&
                                                                                        prompt.discrepancyStatus === ITEM_STATUS_CODES.warn
                                                                                            ? ITEM_STATUS_CODES.status_unchanged
                                                                                            : ITEM_STATUS_CODES.warn;
                                                                                    setDailyServiceLogFormPrompts(updatePrompts);
                                                                                }}
                                                                            >
                                                                                {prompt.hasActiveDiscrepancy && prompt.discrepancyStatus === ITEM_STATUS_CODES.warn
                                                                                    ? `Status Unchanged`
                                                                                    : <Icon name="status-warning" size="medium" />
                                                                                }

                                                                            </MUIButton>
                                                                            <MUIButton
                                                                                variant={!prompt.value || prompt.value === ITEM_STATUS_CODES.inop ? "contained" : "outlined"}
                                                                                color="error"
                                                                                style={{
                                                                                    borderRadius: 20,
                                                                                    padding: "20px 10px",
                                                                                    fontSize: 40,
                                                                                    width: "100%",
                                                                                }}
                                                                                onClick={() => {
                                                                                    const updatePrompts = [...dailyServiceLogFormPrompts];
                                                                                    updatePrompts[idx].value = ITEM_STATUS_CODES.inop;
                                                                                    setDailyServiceLogFormPrompts(updatePrompts);
                                                                                }}
                                                                            >
                                                                                <Icon name="status-negative" size="medium" />
                                                                            </MUIButton>
                                                                            {(!prompt.hasActiveDiscrepancy || prompt.discrepancyStatus !== ITEM_STATUS_CODES.warn) && (
                                                                                <MUIButton
                                                                                    variant={
                                                                                        !prompt.value ||
                                                                                        prompt.value === ITEM_STATUS_CODES.na ||
                                                                                        prompt.value === ITEM_STATUS_CODES.under_review ||
                                                                                        prompt.value === ITEM_STATUS_CODES.status_unchanged
                                                                                            ? "contained"
                                                                                            : "outlined"
                                                                                    }
                                                                                    color={prompt.hasActiveDiscrepancy ? "primary" : "warning"}
                                                                                    sx={{
                                                                                        '&.MuiButton-containedWarning': {
                                                                                            backgroundColor: '#f57c00',
                                                                                            '&:hover': {
                                                                                                backgroundColor: '#ef6c00'
                                                                                            }
                                                                                        }
                                                                                    }}
                                                                                    style={{
                                                                                        borderRadius: 20,
                                                                                        fontSize: '14px',
                                                                                        padding: "20px 10px",
                                                                                        width: "100%",
                                                                                    }}
                                                                                    onClick={() => {
                                                                                        const updatePrompts = [...dailyServiceLogFormPrompts];
                                                                                        if (prompt.hasActiveDiscrepancy) {
                                                                                            updatePrompts[idx].value =
                                                                                                ITEM_STATUS_CODES.status_unchanged;
                                                                                        } else {
                                                                                            // If no active discrepancy, set to N/A
                                                                                            updatePrompts[idx].value = ITEM_STATUS_CODES.na;
                                                                                            activeStepIndex < dailyServiceLogFormPrompts.length - 1
                                                                                                ? setActiveStepIndex(activeStepIndex + 1)
                                                                                                : setShouldReviewForm(true);
                                                                                        }
                                                                                        setDailyServiceLogFormPrompts(updatePrompts);
                                                                                    }}
                                                                                >
                                                                                    {prompt.hasActiveDiscrepancy ? "Status Unchanged" : 'N/A'}
                                                                                </MUIButton>
                                                                            )}
                                                                        </Grid>
                                                                    )}

                                                                    {dailyServiceLogFormPrompts[idx].value &&
                                                                        dailyServiceLogFormPrompts[idx].value !== ITEM_STATUS_CODES.ok &&
                                                                        dailyServiceLogFormPrompts[idx].value !== OPERATOR_RESPONSE_VALUES.added &&
                                                                        (requiresNotesOrMedia(dailyServiceLogFormPrompts[idx]) ||
                                                                            prompt.subType === DEFAULT_FORM_INPUT_TYPES.fourResponse ||
                                                                            prompt.subType === DEFAULT_FORM_INPUT_TYPES.yesNo ||
                                                                            prompt.subType === DEFAULT_FORM_INPUT_TYPES.checkbox ||
                                                                            !prompt.subType) && (
                                                                            <>
                                                                                <FormField label="Notes">
                                                                                    <Textarea
                                                                                        value={prompt.notes || (prompt.hasActiveDiscrepancy ? prompt.discrepancyNotes ?? '' : '')}
                                                                                        onChange={({ detail }) => {
                                                                                            const updatePrompts = [...dailyServiceLogFormPrompts];
                                                                                            updatePrompts[idx].notes = detail.value;
                                                                                            setDailyServiceLogFormPrompts(updatePrompts);
                                                                                        }}
                                                                                    />
                                                                                </FormField>
                                                                                <Container header={<h2>Add Audio Note</h2>}>
                                                                                    <Stack direction="horizontal" size="xxl">
                                                                                        {/* Audio recording functionality remains unchanged */}
                                                                                        {isAudioRecordingStopped(status) && (
                                                                                            <Button
                                                                                                onClick={startRecording}
                                                                                                iconAlign="left"
                                                                                                iconName="microphone"
                                                                                            >
                                                                                                Record
                                                                                            </Button>
                                                                                        )}
                                                                                        {!isAudioRecordingStopped(status) && (
                                                                                            <Button
                                                                                                onClick={stopRecording}
                                                                                                loading={status === "acquiring_media"}
                                                                                                iconAlign="left"
                                                                                                iconName="microphone-off"
                                                                                            >
                                                                                                {status === "recording" && <>End Recording</>}
                                                                                                {status === "acquiring_media" && <>Starting</>}
                                                                                            </Button>
                                                                                        )}
                                                                                        {audioReview[activeStepIndex] && (
                                                                                            <>
                                                                                                <audio
                                                                                                    src={URL.createObjectURL(audioReview[activeStepIndex])}
                                                                                                    controls
                                                                                                >
                                                                                                    <source
                                                                                                        src={URL.createObjectURL(audioReview[activeStepIndex])}
                                                                                                        type="audio/wav"
                                                                                                    />
                                                                                                </audio>
                                                                                                <Button
                                                                                                    iconAlign="left"
                                                                                                    iconName="close"
                                                                                                    onClick={() => {
                                                                                                        const newMedia = { ...media };
                                                                                                        delete newMedia[activeStepIndex];
                                                                                                        setMedia(newMedia);
                                                                                                        const newAudioReview = { ...audioReview };
                                                                                                        delete newAudioReview[activeStepIndex];
                                                                                                        setAudioReview(newAudioReview);
                                                                                                    }}
                                                                                                >
                                                                                                    Remove
                                                                                                </Button>
                                                                                            </>
                                                                                        )}
                                                                                    </Stack>
                                                                                </Container>
                                                                                <Dropzone
                                                                                    addClassNames={'video-dropzone' as IStyleCustomization<string>}
                                                                                    onChangeStatus={handleChangeStatus}
                                                                                    accept="image/jpeg, image/jpg, image/png, audio/wav, audio/mp3, video/*"
                                                                                    inputContent={(_files, extra) => {
                                                                                        if (extra.reject) {
                                                                                            return "Image or video files only";
                                                                                        }
                                                                                        return "Click to add images or videos";
                                                                                    }}
                                                                                />
                                                                            </>
                                                                        )}
                                                                    <Box>
                                                                        <Stack direction="horizontal" size="l">
                                                                            {prompt?.photo?.map((photo, index) => {
                                                                                return (
                                                                                    <div
                                                                                        key={index}
                                                                                        className="photo-container"
                                                                                    >
                                                                                        <img
                                                                                            key={index}
                                                                                            src={URL.createObjectURL(photo?.data?.file as unknown as MediaSource)}
                                                                                            alt={prompt.label}
                                                                                            className="photo"
                                                                                        />
                                                                                        <div className="delete-image">
                                                                                            <Button
                                                                                                variant="icon"
                                                                                                iconName="close"
                                                                                                onClick={async () => {
                                                                                                    await deleteRecord(photo?.id as Key)
                                                                                                    const updatedFormPrompts = [
                                                                                                        ...dailyServiceLogFormPrompts,
                                                                                                    ];
                                                                                                    updatedFormPrompts[
                                                                                                        idx
                                                                                                    ].photo?.splice(index, 1);
                                                                                                    if (media) {
                                                                                                        setMedia((prev) => {
                                                                                                            return {
                                                                                                                ...prev,
                                                                                                                [activeStepIndex]: media[activeStepIndex].filter(
                                                                                                                    (el) => el.id !== photo?.id
                                                                                                                ),
                                                                                                            };
                                                                                                        });
                                                                                                    }
                                                                                                    setDailyServiceLogFormPrompts(
                                                                                                        updatedFormPrompts
                                                                                                    );
                                                                                                }}
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                )
                                                                            })}
                                                                        </Stack>
                                                                    </Box>
                                                                </Stack>
                                                            </Container>
                                                        </>
                                                    )}

                                                </Box>
                                            );
                                        })}
                                    </Form>
                                </form>
                            </Stack>
                        </>
                    )}
                </ThemeProvider>
            }
        />
    );
};
